"use client";

import React from "react";
import { Check, X, Shield, MapPin, HardHat, Bike, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tiers = [{
  name: "Individual",
  alias: "Adult (19-64)",
  price: "$90",
  period: "/ month",
  description: "Comprehensive primary care designed for your active lifestyle and wellness.",
  highlight: false,
  features: [{
    name: "No Co-pays",
    included: true,
    icon: Shield
  }, {
    name: "Unlimited Visits",
    included: true,
    icon: Bike
  }, {
    name: "Same-day Access",
    included: true,
    icon: HardHat
  }, {
    name: "Direct Texting",
    included: true,
    icon: MapPin
  }],
  cta: "Start Your Care"
}, {
  name: "Family",
  alias: "Community",
  price: "$250",
  period: "/ month",
  description: "Complete peace of mind for two adults and all children in your household.",
  highlight: true,
  features: [{
    name: "No Co-pays",
    included: true,
    icon: Shield
  }, {
    name: "Unlimited Visits",
    included: true,
    icon: Bike
  }, {
    name: "Same-day Access",
    included: true,
    icon: HardHat
  }, {
    name: "Direct Texting",
    included: true,
    icon: MapPin
  }],
  cta: "Secure Family Plan"
}, {
  name: "Senior",
  alias: "Legacy (65+)",
  price: "$110",
  period: "/ month",
  description: "Personalized attention and dedicated chronic disease management for seniors.",
  highlight: false,
  features: [{
    name: "No Co-pays",
    included: true,
    icon: Shield
  }, {
    name: "Unlimited Visits",
    included: true,
    icon: Bike
  }, {
    name: "Same-day Access",
    included: true,
    icon: HardHat
  }, {
    name: "Direct Texting",
    included: true,
    icon: MapPin
  }],
  cta: "Begin Your Legacy"
}, {
  name: "Kids",
  alias: "KIDS (4-18)",
  price: "$50",
  period: "/ month",
  description: "Tailored primary care for children.",
  highlight: false,
  features: [{
    name: "No Co-pays",
    included: true,
    icon: Shield
  }, {
    name: "Unlimited Visits",
    included: true,
    icon: Bike
  }, {
    name: "Same-day Access",
    included: true,
    icon: HardHat
  }, {
    name: "Direct Texting",
    included: true,
    icon: MapPin
  }],
  cta: "Start Kids Care"
}];
export default function RentalTiers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="8702" id="transparent-membership" ref={ref} className="bg-background py-20 px-4 transition-opacity duration-700 opacity-100 translate-y-0 !text-[hsl(223,23%,43%)] md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-normal tracking-tight font-serif">Transparent <span className="!text-[hsl(223,23%,43%)]" data-ast-id="f:/src/pages/Home/sections/RentalTiers.tsx|sig:xov6rt" data-ast-start="3412" data-ast-end="3472">Membership Pricing</span></h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans">
            Crescent Direct Primary Care offers a simple, insurance-free model. No hidden fees, no co-pays—just high-quality healthcare when you need it most.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, i) => <div key={tier.name} data-index={i} className="h-full">
              <Card className={`h-full flex flex-col relative transition-all duration-400 hover:-translate-y-2 rounded-xl ${tier.highlight ? "border-primary shadow-xl ring-1 ring-primary/10 bg-card/90 backdrop-blur-md z-10 scale-105 md:scale-110" : "bg-card/40 hover:bg-card/70 border-border shadow-sm"}`}>
                {tier.highlight && <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground hover:bg-primary px-4 py-1 text-xs uppercase tracking-widest font-mono shadow-md">
                      Most Popular
                    </Badge>
                  </div>}

                <CardHeader className="text-center pb-2 pt-8">
                  <div className="mb-2">
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      {tier.alias}
                    </span>
                  </div>
                  <CardTitle className="text-3xl font-serif font-normal tracking-tight">
                    {tier.name}
                  </CardTitle>
                  <CardDescription className="mt-2 min-h-[3rem] font-sans text-sm">
                    {tier.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow">
                  <div className="flex items-baseline justify-center my-6">
                    <span className="text-5xl font-normal text-foreground tracking-tighter font-serif">
                      {tier.price}
                    </span>
                    <span className="text-muted-foreground ml-2 font-medium font-sans">
                      {tier.period}
                    </span>
                  </div>

                  <div className="rounded-lg border border-border/40 bg-muted/20 overflow-hidden">
                    <Table>
                      <TableBody>
                        {tier.features.map((feature, idx) => <TableRow key={idx} className="border-b border-border/30 last:border-0 hover:bg-transparent">
                            <TableCell className="py-3 font-medium flex items-center gap-3 font-sans text-sm">
                              <feature.icon className="size-4 text-muted-foreground opacity-70" />
                              {feature.name}
                            </TableCell>
                            <TableCell className="text-right py-3">
                              {feature.included ? <Check className="size-5 text-primary ml-auto" /> : <X className="size-5 text-muted-foreground/20 ml-auto" />}
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>

                <CardFooter className="pt-4 pb-8">
                  <Button asChild className={`w-full text-sm h-12 font-medium uppercase tracking-widest rounded-full transition-colors duration-400 ${tier.highlight ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg" : "bg-muted text-foreground hover:bg-muted/80"}`}>
                    <Link to="/RegisterPage">{tier.cta}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}